/**
 * Contratado × faturado na tela de contratos (issue #728).
 *
 * O contrato mostra MRR de TABELA (preço cheio dos itens ativos). O que o
 * cliente pagou no mês vem das faturas, e é lá que aparecem pró-rata de criação,
 * estorno de deleção e resize. A divergência entre os dois é o sinal útil — por
 * isso ela é calculada aqui, num lugar só, e não repetida em cada tela.
 *
 * Regra que não pode se perder: contrato SEM fatura mostra "—", nunca R$ 0,00.
 * Zero de fallback exibido como dado real foi exatamente o bug da #726.
 */

const MESES_CURTOS = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
]

/** Divergências abaixo de meio centavo são ruído de arredondamento, não sinal. */
const TOLERANCIA = 0.005

export const EM_BRANCO = '—'

export type BilledInvoiceRef = {
  billing_period: string // "YYYY-MM"
  total_amount: number
  currency?: string | null
}

export type BilledDirection = 'up' | 'down' | 'equal'

export type BilledSummary = {
  /** Existe fatura? Falso ⇒ tudo é "—", nada de zero travestido de dado. */
  hasInvoice: boolean
  /**
   * O faturado pode ser comparado com o MRR contratado?
   *
   * Falso ⇒ o valor é real, mas a divergência seria inventada. Distinguir isso de
   * `direction === 'equal'` importa: "não dá para comparar" não é "está igual".
   */
  comparable: boolean
  /** "jul/26" — sempre explícito: o mês faturado NÃO é necessariamente o anterior. */
  periodLabel: string | null
  /** "R$ 1.115,20" ou "—". */
  billedText: string
  /** faturado − contratado (null sem fatura). */
  delta: number | null
  /** Diferença absoluta formatada, ou null quando não há divergência. */
  deltaText: string | null
  direction: BilledDirection | null
}

/**
 * "2026-07" → "jul/26". Entrada fora do formato volta como veio (não inventa mês).
 */
export const formatShortPeriod = (billingPeriod?: string | null): string => {
  if (!billingPeriod) return EM_BRANCO
  const match = /^(\d{4})-(\d{2})$/.exec(billingPeriod)
  if (!match) return billingPeriod
  const [, year, month] = match
  const idx = Number.parseInt(month, 10) - 1
  if (idx < 0 || idx > 11) return billingPeriod
  return `${MESES_CURTOS[idx]}/${year.slice(2)}`
}

export const formatMoney = (value: number, currency = 'BRL'): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(value)

/**
 * Tarifa UNITÁRIA (ex.: excedente por vCPU/GB/mês do contrato de bloco, `Numeric(10,4)`): até 4
 * casas, nunca menos de 2. Formatá-la como dinheiro mostraria "R$ 0,00" para 0,0040 enquanto a
 * cobrança usa as 4 casas (#1091 F3). Total continua em centavos, por `formatMoney`.
 */
export const formatUnitRate = (value: number, currency = 'BRL'): string =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value)

/** Texto do porquê de um faturado não ser comparável (motivos vindos da API). */
export const INCOMPARABLE_REASONS: Record<string, string> = {
  contract_inactive:
    'Contrato encerrado: o MRR de tabela é zero, então a fatura inteira apareceria como divergência. O valor faturado continua real.',
  provider_contract:
    'A fatura do provedor é o maior entre a mensalidade fixa e o consumo base — não se compara com o MRR de tabela deste contrato.',
  multiple_active_contracts:
    'A fatura é do par cliente↔revenda e há mais de um contrato ativo entre vocês, então a comparação com o MRR de um contrato só não se aplica.',
  invoice_before_contract:
    'Esta fatura é anterior ao início do contrato atual — ela pertence ao contrato anterior do mesmo par.',
}

export const incomparableReasonText = (reason?: string | null): string | undefined =>
  (reason && INCOMPARABLE_REASONS[reason]) || undefined

/**
 * A régua por MÊS, a partir do que o backend decidiu para o contrato.
 *
 * `base` já cobre contrato encerrado, contrato do provedor e par com dois
 * contratos ativos. Falta o recorte temporal, que depende do mês exibido: uma
 * fatura anterior ao início do contrato é do contrato anterior do mesmo par.
 * Comparação lexicográfica basta — "YYYY-MM" ordena como data.
 */
export const isBilledComparable = (
  base: boolean,
  contractStartPeriod: string | null | undefined,
  billingPeriod: string,
): boolean => base && (!contractStartPeriod || billingPeriod >= contractStartPeriod)

export type SummarizeBilledOptions = {
  /**
   * Moeda do contrato (default BRL). Se divergir da moeda da fatura não há
   * divergência a mostrar: subtrair valores de moedas diferentes produz um
   * número sem significado, formatado com uma das duas.
   */
  contractedCurrency?: string
  /**
   * O chamador já sabe que a comparação não se aplica? Dois casos conhecidos:
   * o par tem mais de um contrato ativo (a fatura é do PAR, não do contrato), e
   * o contrato com o provedor, cuja fatura é max(mensalidade, consumo base) — e
   * não a soma de itens de tabela. Nos dois, o valor é real e o delta seria
   * inventado.
   */
  comparable?: boolean
}

/**
 * Compara o MRR contratado com a última fatura do par.
 *
 * @param contracted MRR de tabela do contrato (`total_monthly_cost`)
 * @param invoice    fatura mais recente do par, ou null/undefined se não há
 * @param options    ver `SummarizeBilledOptions`
 */
export const summarizeBilled = (
  contracted: number,
  invoice?: BilledInvoiceRef | null,
  options: SummarizeBilledOptions = {},
): BilledSummary => {
  const { contractedCurrency = 'BRL', comparable: allowed = true } = options

  if (!invoice) {
    return {
      hasInvoice: false,
      comparable: false,
      periodLabel: null,
      billedText: EM_BRANCO,
      delta: null,
      deltaText: null,
      direction: null,
    }
  }

  const currency = invoice.currency || 'BRL'
  const comparable = allowed && currency === contractedCurrency

  if (!comparable) {
    return {
      hasInvoice: true,
      comparable: false,
      periodLabel: formatShortPeriod(invoice.billing_period),
      billedText: formatMoney(invoice.total_amount, currency),
      delta: null,
      deltaText: null,
      direction: null,
    }
  }

  const delta = invoice.total_amount - contracted
  const direction: BilledDirection =
    Math.abs(delta) < TOLERANCIA ? 'equal' : delta > 0 ? 'up' : 'down'

  return {
    hasInvoice: true,
    comparable: true,
    periodLabel: formatShortPeriod(invoice.billing_period),
    billedText: formatMoney(invoice.total_amount, currency),
    delta,
    deltaText: direction === 'equal' ? null : formatMoney(Math.abs(delta), currency),
    direction,
  }
}
